import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "../../../hooks/useToastActionWrapper";
import { toVisibility } from "../../../hooks/useStationVisibility";
import {
  listExclusions,
  removeExclusion,
  updateExclusionNote,
} from "../../../services/stationExclusions";
import { StationExclusion, ViewState } from "../../../types/alerting";
import { actorLabel } from "../../../utils/actors";
import { formatDuration } from "../../../utils/formatDuration";
import { formatInstant } from "../../../utils/instantFormat";
import StationVisibilityTag from "../StationStatusDetail/StationVisibilityTag";
import EditExclusionNoteDialog from "./EditExclusionNoteDialog";
import { REFUSED_MESSAGE, REMOVAL_WARNING, errorMessageOf, isRefusal } from "./exclusions";

const StationExclusionListPage = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const { toastActionWrapper } = useToastActionWrapper(toast);

  const [state, setState] = useState<ViewState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [exclusions, setExclusions] = useState<StationExclusion[]>([]);
  const [selection, setSelection] = useState<StationExclusion[]>([]);
  const [editing, setEditing] = useState<StationExclusion | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  /** Keeps the current rows on screen while reloading, so a change does not flash the spinner. */
  const load = useCallback(async () => {
    try {
      const data = await listExclusions();
      setExclusions(data);
      setState(data.length > 0 ? "ready" : "empty");
    } catch (e) {
      setError(errorMessageOf(e));
      setState(isRefusal(e) ? "refused" : "failed");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (stationIds: string[]) => {
    await toastActionWrapper({
      action: () => Promise.all(stationIds.map((id) => removeExclusion(id))),
      successMessage:
        stationIds.length === 1
          ? `${stationIds[0]} removed from the exclusion list.`
          : `${stationIds.length} stations removed from the exclusion list.`,
      errorMessage: "Could not remove every station from the exclusion list. Please try again.",
    });
    setSelection([]);
    await load();
  };

  const confirmRemove = (stationIds: string[]) =>
    confirmDialog({
      header: "Remove from exclusion list",
      message: REMOVAL_WARNING,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger",
      accept: () => {
        void remove(stationIds);
      },
    });

  const openEdit = (exclusion: StationExclusion) => {
    setNoteError(null);
    setEditing(exclusion);
  };

  const saveNote = async (note: string | null) => {
    if (!editing) return;
    const { stationId } = editing;
    setSavingNote(true);
    setNoteError(null);
    const ok = await toastActionWrapper({
      action: async () => {
        try {
          await updateExclusionNote(stationId, note);
        } catch (e) {
          setNoteError(errorMessageOf(e));
          throw e;
        }
      },
      successMessage: `Note on ${stationId} saved.`,
      errorMessage: "Could not save the note.",
    });
    setSavingNote(false);
    if (ok) {
      setEditing(null);
      await load();
    }
  };

  const stationBody = (e: StationExclusion) => (
    <div className="flex flex-column align-items-start">
      <Button
        link
        label={e.stationId}
        className="p-0"
        onClick={() => navigate(`/alerting/status/LOCATION/${encodeURIComponent(e.stationId)}`)}
      />
      <span className="text-sm text-color-secondary">
        {e.stationFound ? e.stationName ?? "—" : "No longer found"}
      </span>
    </div>
  );

  const noteBody = (e: StationExclusion) => (
    <div className="flex align-items-center gap-2">
      <span className={e.note ? undefined : "text-color-secondary"}>{e.note ?? "—"}</span>
      <Button
        icon="pi pi-pencil"
        rounded
        text
        aria-label={`Edit note for ${e.stationId}`}
        onClick={() => openEdit(e)}
      />
    </div>
  );

  const removeBody = (e: StationExclusion) => (
    <Button
      icon="pi pi-trash"
      rounded
      text
      severity="danger"
      aria-label={`Remove ${e.stationId}`}
      onClick={() => confirmRemove([e.stationId])}
    />
  );

  const renderBody = () => {
    if (state === "loading") {
      return (
        <div className="flex align-items-center gap-3 py-5" data-testid="view-state-loading">
          <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
          <span className="text-color-secondary">Loading…</span>
        </div>
      );
    }
    if (state === "refused") {
      return <Message severity="warn" text={REFUSED_MESSAGE} />;
    }
    if (state === "failed") {
      return <Message severity="error" text={`Could not load the excluded stations: ${error}`} />;
    }
    if (state === "empty") {
      return <Message severity="info" text="No stations are excluded." />;
    }

    return (
      <>
        <div className="flex mb-3">
          <Button
            label="Remove selected"
            icon="pi pi-trash"
            severity="danger"
            outlined
            disabled={selection.length === 0}
            onClick={() => confirmRemove(selection.map((s) => s.stationId))}
          />
        </div>

        <DataTable
          value={exclusions}
          dataKey="stationId"
          selectionMode="checkbox"
          selection={selection}
          onSelectionChange={(e) => setSelection(e.value as StationExclusion[])}
          selectionAriaLabel="stationId"
          paginator
          rows={25}
          emptyMessage="No stations are excluded."
          className="p-datatable-sm"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
          <Column header="Station" body={stationBody} />
          <Column
            header="Visibility"
            body={(e: StationExclusion) => (
              <StationVisibilityTag visibility={toVisibility(e.published)} />
            )}
          />
          <Column
            header="Excluded for"
            body={(e: StationExclusion) => formatDuration(e.excludedForSeconds)}
          />
          <Column header="Since" body={(e: StationExclusion) => formatInstant(e.excludedAt)} />
          <Column header="Added by" body={(e: StationExclusion) => actorLabel(e.excludedBy)} />
          <Column header="Note" body={noteBody} />
          <Column header="Remove" body={removeBody} style={{ width: "6rem" }} />
        </DataTable>
      </>
    );
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Excluded stations</div>
          <p className="text-color-secondary mt-2 mb-0">
            Owners of these stations get no timeout alerts, reminders or back-online messages. Their
            status is still recorded.
          </p>
        </div>
        <Button
          label="Add stations"
          icon="pi pi-plus"
          onClick={() => navigate("/alerting/exclusions/add")}
        />
      </div>

      {renderBody()}

      <EditExclusionNoteDialog
        exclusion={editing}
        saving={savingNote}
        errorMessage={noteError}
        onSave={saveNote}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};

export default StationExclusionListPage;
